import { nanoid } from 'nanoid';
import Yup from 'utils/validation/yup-extensions';
import {
	INITIAL_VALUES as DEPARTMENT_INITIAL_VALUES,
	VALIDATION_SCHEMA as DEPARTMENT_VALIDATION_SCHEMA,
	FORM_FIELD_KEYS as DEPARTMENT_FORM_FIELD_KEYS,
} from 'pages/Bridges/ManagerValidation/components/Supplements/DepartmentSupplement';
import {
	VALIDATION_SCHEMA as RESEARCH_VALIDATION_SCHEMA,
	FORM_FIELD_KEYS as RESEARCH_FORM_FIELD_KEYS,
} from 'pages/Bridges/ManagerValidation/components/Supplements/ResearchSupplement';
import {
	VALIDATION_SCHEMA as BILLING_VALIDATION_SCHEMA,
	FORM_FIELD_KEYS as BILLING_FORM_FIELD_KEYS,
} from 'pages/Bridges/ManagerValidation/components/Supplements/BillingSupplement';
import {
	VALIDATION_SCHEMA as RADIOLOGY_VALIDATION_SCHEMA,
	FORM_FIELD_KEYS as RADIOLOGY_FORM_FIELD_KEYS,
} from 'pages/Bridges/ManagerValidation/components/Supplements/RadiologySupplement';
import {
	VALIDATION_SCHEMA as ANESTHESIOLOGY_VALIDATION_SCHEMA,
	FORM_FIELD_KEYS as ANESTHESIOLOGY_FORM_FIELD_KEYS,
} from 'pages/Bridges/ManagerValidation/components/Supplements/AnesthesiologySupplement';
import {
	VALIDATION_SCHEMA as SPECIALTIES_VALIDATION_SCHEMA,
	FORM_FIELD_KEYS as SPECIALTIES_FORM_FIELD_KEYS,
	INITIAL_VALUES as SPECIALTIES_INITIAL_VALUES,
} from 'pages/Bridges/ManagerValidation/components/Supplements/SpecialtySupplement';
/**
 * T_Component FI_ID for the "form" component
 */
export const EPIC_APP_ID = 1616;

/**
 * T_Component FI_ID for the "form" component
 */
export const EPIC_COMPONENT_ID = 5532;

export const MAX_COUNTS = {
	JOB_CATEGORIES: 7,
};

/**
 * Keys to be used by Formik
 */
export const EPIC_FIELD_KEYS = {
	COMMENTS: 'comments',
	JOB_CATEGORIES: 'jobCategories',
	HAS_SUPPLEMENTS: 'hasSupplements',
	NEED_DEPARTMENT: 'needDepartment',
	DEPARTMENT: DEPARTMENT_FORM_FIELD_KEYS.DEPARTMENTS,
	NPI: 'npi',
	NEED_NPI: 'needNpi',
	SPECIALTIES: 'specialties',
	NEED_SPECIALTIES: 'needSpecialties',
	NPI_VERIFICATION: 'npiVerification',
	NEED_BILLING: 'needBilling',
	BILLING: 'BILLING',
	BILLING_NEEDS: 'BILLING_NEEDS',
	NEED_RESEARCH: 'needResearch',
	RESEARCH_CITI_TRAINING: 'RESEARCH_CITI_TRAINING',
	RESEARCH_CITI_TRAINING_DATE: 'RESEARCH_CITI_TRAINING_DATE',
	RESEARCH_STUDY_TYPE: 'RESEARCH_STUDY_TYPE',
	RESEARCH_STUDY_ID: 'RESEARCH_STUDY_ID',
	SECTION_MAIN: 'sectionMain',
	TRACKING_NUMBER: 'TRACKING_NUMBER',
	NEED_RADIOLOGY: 'needRadiology',
	RADIOLOGY_VENDOR: 'RADIOLOGY_VENDOR',
	RADIOLOGY_OTHER: 'RADIOLOGY_OTHER',
	NEED_ANESTHESIOLOGY: 'needAnesthesiology',
	ANESTHESIOLOGY_VENDOR: 'ANESTHESIOLOGY_VENDOR',
	ANESTHESIOLOGY_OTHER: 'ANESTHESIOLOGY_OTHER',
};

/**
 * Form field metadata (default values, validation schema).
 * ENSURE ORDER MATCHES BACKEND ORDER (otherwise, pre-populated MOD requests are subject to allow submissions even if no change is made).
 */
export const EPIC_FIELDS = {
	[EPIC_FIELD_KEYS.COMMENTS]: {
		defaultValue: '',
		validationSchema: Yup.string().max(
			100,
			'You have exceeded the character count (100).',
		),
	},
	[EPIC_FIELD_KEYS.JOB_CATEGORIES]: {
		defaultValue: [
			{
				jobCategoryId: nanoid(),
				jobCategoryName: null,
				jobCategoryType: null,
				jobCategoryExperience: 'New',
				active: true,
				wave: null,
			},
		],
		validationSchema: Yup.array()
			.of(
				Yup.object()
					.shape({
						jobCategoryId: Yup.mixed(),
						jobCategoryName: Yup.string()
							.test(
								'active-job-category-name',
								'Please select a job category.',
								function (value) {
									if (this.parent?.active === false) {
										return true;
									}

									return !!String(value ?? '').trim();
								},
							)
							.nullable(),
						jobCategoryType: Yup.string().nullable(),
						jobCategoryExperience: Yup.string()
							.test(
								'active-job-category-experience',
								'Please specify experience',
								function (value) {
									if (this.parent?.active === false) {
										return true;
									}

									return !!String(value ?? '').trim();
								},
							)
							.nullable(),
						wave: Yup.number()
							.test(
								'active-job-category-wave',
								'Please specify a wave',
								function (value) {
									if (this.parent?.active === false) {
										return true;
									}

									return (
										value !== null && value !== undefined
									);
								},
							)
							.nullable(),
					})
					.default(null)
					.nullable(),
			)
			.min(1, 'You must have at least one job category.')
			.max(
				MAX_COUNTS.JOB_CATEGORIES,
				'You can select no more than ' +
					MAX_COUNTS.JOB_CATEGORIES +
					' job categories',
			)
			.test('enhancement count', async function (value) {
				if (value && value.length > 0) {
					const activeJobCategories = value.filter(
						jc => jc?.active !== false,
					);

					const primaryJCs = activeJobCategories.filter(
						jc =>
							jc?.jobCategoryType?.toLowerCase() ===
							'primary category',
					);
					const enhancementJCs = activeJobCategories.filter(
						jc =>
							jc?.jobCategoryType?.toLowerCase() ===
							'enhancement category',
					);
					if (enhancementJCs.length > 0 && primaryJCs.length === 0) {
						return this.createError({
							message:
								'You must have at least one primary job category',
						});
					}
				}
				return true;
			}),
	},
	[EPIC_FIELD_KEYS.HAS_SUPPLEMENTS]: {
		defaultValue: [],
		validationSchema: Yup.array(),
	},
	[EPIC_FIELD_KEYS.NEED_DEPARTMENT]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.DEPARTMENT]: {
		defaultValue:
			DEPARTMENT_INITIAL_VALUES[DEPARTMENT_FORM_FIELD_KEYS.DEPARTMENTS],
		validationSchema: Yup.array().when(EPIC_FIELD_KEYS.NEED_DEPARTMENT, {
			is: true,
			then: () =>
				DEPARTMENT_VALIDATION_SCHEMA[
					DEPARTMENT_FORM_FIELD_KEYS.DEPARTMENTS
				],
		}),
	},
	[EPIC_FIELD_KEYS.NEED_NPI]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.NPI]: {
		defaultValue: '',
		// validation schema defined in component context due to dependency on non-formik variables
	},
	[EPIC_FIELD_KEYS.NPI_VERIFICATION]: {
		defaultValue: false,
		validationSchema: Yup.bool().when(EPIC_FIELD_KEYS.NEED_NPI, {
			is: true,
			then: schema =>
				schema
					.oneOf([true], 'Please acknowledge.')
					.required('Please acknowledge.'),
		}),
	},
	[EPIC_FIELD_KEYS.NEED_BILLING]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.BILLING]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_BILLING, {
			is: true,
			then: () =>
				BILLING_VALIDATION_SCHEMA[BILLING_FORM_FIELD_KEYS.BILLING],
		}),
	},
	[EPIC_FIELD_KEYS.BILLING_NEEDS]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_BILLING, {
			is: true,
			then: () =>
				BILLING_VALIDATION_SCHEMA[
					BILLING_FORM_FIELD_KEYS.BILLING_NEEDS
				],
		}),
	},
	[EPIC_FIELD_KEYS.NEED_RADIOLOGY]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.RADIOLOGY_VENDOR]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RADIOLOGY, {
			is: true,
			then: () =>
				RADIOLOGY_VALIDATION_SCHEMA[
					RADIOLOGY_FORM_FIELD_KEYS.RADIOLOGY_VENDOR
				],
		}),
	},
	[EPIC_FIELD_KEYS.RADIOLOGY_OTHER]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RADIOLOGY, {
			is: true,
			then: () =>
				RADIOLOGY_VALIDATION_SCHEMA[
					RADIOLOGY_FORM_FIELD_KEYS.RADIOLOGY_OTHER
				],
		}),
	},
	[EPIC_FIELD_KEYS.NEED_ANESTHESIOLOGY]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.ANESTHESIOLOGY_VENDOR]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			EPIC_FIELD_KEYS.NEED_ANESTHESIOLOGY,
			{
				is: true,
				then: () =>
					ANESTHESIOLOGY_VALIDATION_SCHEMA[
						ANESTHESIOLOGY_FORM_FIELD_KEYS.ANESTHESIOLOGY_VENDOR
					],
			},
		),
	},
	[EPIC_FIELD_KEYS.ANESTHESIOLOGY_OTHER]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			EPIC_FIELD_KEYS.NEED_ANESTHESIOLOGY,
			{
				is: true,
				then: () =>
					ANESTHESIOLOGY_VALIDATION_SCHEMA[
						ANESTHESIOLOGY_FORM_FIELD_KEYS.ANESTHESIOLOGY_OTHER
					],
			},
		),
	},
	[EPIC_FIELD_KEYS.NEED_SPECIALTIES]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.SPECIALTIES]: {
		defaultValue:
			SPECIALTIES_INITIAL_VALUES[SPECIALTIES_FORM_FIELD_KEYS.SPECIALTIES],
		validationSchema: Yup.array()
			.nullable()
			.when(EPIC_FIELD_KEYS.NEED_SPECIALTIES, {
				is: true,
				then: () =>
					SPECIALTIES_VALIDATION_SCHEMA[
						SPECIALTIES_FORM_FIELD_KEYS.SPECIALTIES
					],
			}),
	},
	[EPIC_FIELD_KEYS.NEED_RESEARCH]: {
		defaultValue: false,
		validationSchema: Yup.bool(),
	},
	[EPIC_FIELD_KEYS.RESEARCH_CITI_TRAINING]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RESEARCH, {
			is: true,
			then: () =>
				RESEARCH_VALIDATION_SCHEMA[
					RESEARCH_FORM_FIELD_KEYS.RESEARCH_CITI_TRAINING
				],
		}),
	},
	[EPIC_FIELD_KEYS.RESEARCH_CITI_TRAINING_DATE]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RESEARCH, {
			is: true,
			then: () =>
				RESEARCH_VALIDATION_SCHEMA[
					RESEARCH_FORM_FIELD_KEYS.RESEARCH_CITI_TRAINING_DATE
				],
		}),
	},
	[EPIC_FIELD_KEYS.RESEARCH_STUDY_TYPE]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RESEARCH, {
			is: true,
			then: () =>
				RESEARCH_VALIDATION_SCHEMA[
					RESEARCH_FORM_FIELD_KEYS.RESEARCH_STUDY_TYPE
				],
		}),
	},
	[EPIC_FIELD_KEYS.RESEARCH_STUDY_ID]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.NEED_RESEARCH, {
			is: true,
			then: () =>
				RESEARCH_VALIDATION_SCHEMA[
					RESEARCH_FORM_FIELD_KEYS.RESEARCH_STUDY_ID
				],
		}),
	},
};

export const TEMPLATE_BUILDER_ACCESS_RESTRICTIONS = {
	id: 1071,
	name: 'Template Builder - Scheduling',
};

export const templateBuilderFilter = (jc, isAffiliateDepartment) => {
	return isAffiliateDepartment || !shouldFilterJobCategory(jc);
};

export const shouldFilterJobCategory = jc => {
	return (
		jc.jobCategoryId === TEMPLATE_BUILDER_ACCESS_RESTRICTIONS.id ||
		jc.jobCategoryName?.toLocaleLowerCase() ===
			TEMPLATE_BUILDER_ACCESS_RESTRICTIONS.name.toLocaleLowerCase()
	);
};

export const NO_ACCESS_OR_TRAINING_NEEDED_JOB_CATEGORIES = [
	{
		id: 1426,
		name: 'No Access or Training Needed',
	},
	{
		id: 100000,
		name: 'No Access or Training Needed',
	},
];
